import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

const SUPPORT_EMAIL = "support@velopaths.com";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          <div className="bg-white rounded-xl px-4 py-3 inline-flex">
            <img src="/velopaths-logo.png" alt="Velopaths" className="h-9 w-auto" />
          </div>
        </Link>
        <div className="rounded-2xl border border-border bg-card p-8" data-testid="reset-password-card">
          {!token ? (
            <div className="text-center space-y-3">
              <h1 className="font-heading text-2xl font-bold">Invalid link</h1>
              <p className="text-sm text-muted-foreground">This password reset link is missing or malformed. Please request a new one from the sign-in page.</p>
              <Button onClick={() => navigate("/login")} data-testid="reset-back-to-login">Back to sign in</Button>
            </div>
          ) : done ? (
            <div className="text-center space-y-3" data-testid="reset-success">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
              <h1 className="font-heading text-2xl font-bold">Password updated</h1>
              <p className="text-sm text-muted-foreground">Your password has been reset. You can now sign in with your new password.</p>
              <Button onClick={() => navigate("/login")} className="w-full" data-testid="reset-go-login">Sign in</Button>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold mb-1">Choose a new password</h1>
              <p className="text-sm text-muted-foreground mb-6">Pick a strong password you don't use elsewhere.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="new-password">New password</Label>
                  <div className="relative mt-1.5">
                    <Input id="new-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="reset-password-input" className="pr-10" />
                    <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"} data-testid="reset-toggle-password"
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm password</Label>
                  <Input id="confirm-password" type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} data-testid="reset-confirm-input" className="mt-1.5" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-button">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Reset password
                </Button>
              </form>
            </>
          )}
          <div className="mt-6 text-xs text-center text-muted-foreground flex items-center justify-center gap-1.5">
            <Mail className="w-3.5 h-3.5" />Need help? <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">Contact Support</a>
          </div>
        </div>
      </div>
    </div>
  );
}
